import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cloneDeep } from "lodash";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import {
  DocumentOfReceive,
  ReceiveStatus,
} from "../../models/DocumentOfReceive";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { RoleType } from "../../models/Role";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { getRepromaterialInputTemplate } from "../../document-templates/RepromaterialInputTemplate";
import { activeUser } from "../../services/AuthService";
import {
  getOneDisposition,
  getOneDocument,
} from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllPartners } from "../../services/PartnerService";
import "./NewDocumentOfReceiveRepromaterialPage.scss";

export const NewDocumentOfReceiveRepromaterialPreviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const toast = useRef(null);

  const unwrapArray = (resp: any): any[] => {
    const d = resp?.data;
    if (d?.result && Array.isArray(d.result)) return d.result;
    if (Array.isArray(d)) return d;
    return [];
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [warehousesList, setWarehousesList] = useState<Warehouse[]>([]);
  const [partnersList, setPartnersList] = useState<Partner[]>([]);
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [newDocumentOfReceive, setNewDocumentOfReceive] =
    useState<DocumentOfReceive>({
      id: id ?? "",
      warehouse:
        activeUser?.role?.name === RoleType.Warehouseman
          ? (cloneDeep(activeUser.warehouse) as Warehouse)
          : ({} as Warehouse),
      warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
      type: undefined,
      date: new Date(),
      time: new Date(),
      repromaterialProducts: [],
      repromaterialProductsJSON: "",
      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "",
      vehicleNumber: "",
      note: "",
      foreignProduct: false,
      productOwner: {} as Partner,
      finishedDocument: false,
      finishedDocumentFirstTime: false,
    });

  const [productsDataTable, setProductsDataTable] = useState<Product[]>([]);

  useEffect(() => {
    const rows: Product[] = [];
    const items = newDocumentOfReceive.repromaterialProducts ?? [];

    for (const productItem of items) {
      const p = productsList.find((x) => x.id === productItem.id);
      rows.push({
        id: p?.id ?? "",
        type: ProductType.RawMaterial,
        name: p?.name ?? "",
        code: p?.name ?? "",
        category: (p?.category as ProductCategory) ?? ({} as ProductCategory),
        lot: productItem?.lot ?? "",
        amount: productItem?.amount ?? "",
        measuringUnit: p?.measuringUnit ?? "",
      });
    }
    setProductsDataTable(rows);
  }, [newDocumentOfReceive.repromaterialProducts, productsList]);

  const [pdfData, setPdfData] = useState<any>(null);

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const [whResp, partnersResp, prodResp] = await Promise.all([
          getAllWarehouses(),
          getAllPartners(),
          getAllProducts(),
        ]);

        const wh = unwrapArray(whResp) as Warehouse[];
        const pa = unwrapArray(partnersResp) as Partner[];
        const pr = unwrapArray(prodResp) as Product[];

        setWarehousesList(wh);
        setPartnersList(pa);
        setProductsList(pr);

        if (!id) return;

        const resp = await getOneDocument(id as string);
        const document: any = resp?.data?.result ?? resp?.data;

        if (!document) {
          // @ts-ignore
          toast.current?.show({
            severity: "error",
            detail: t("Greška pri učitavanju dokumenta"),
            life: 3000,
          });
          return;
        }

        // normalize dates
        if (document.date) {
          document.date = new Date(document.date);
          if (document.status === null) document.date = new Date();
        }
        if (document.time) {
          document.time = new Date(document.time);
          if (document.status === null) document.time = new Date();
        }
        if (!document.warehouseman) {
          document.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
        }

        // disposition -> repromaterialProducts (kao referenca)
        if (
          document.dispositionId &&
          document.status !== ReceiveStatus.Arrived
        ) {
          try {
            const oneDisposition = await getOneDisposition(
              document.dispositionId,
            );
            const allProductsJson = cloneDeep(
              oneDisposition.data.result
                ?.repromaterialDispositionProductsJSON as string,
            );
            const dispProducts = JSON.parse(allProductsJson);

            const repromaterialProducts = dispProducts
              .filter((pi: any) => parseFloat(pi?.amount) > 0)
              .map((pi: any) => {
                const p = pr.find((x) => x.id === pi?.id);
                return {
                  id: pi?.id ?? "",
                  type: ProductType.RawMaterial,
                  name: p?.name ?? "",
                  code: p?.name ?? "",
                  category: pi?.category as ProductCategory,
                  lot: "",
                  amount: pi?.amount ?? "",
                  measuringUnit: p?.measuringUnit ?? "",
                  germination: pi?.germination ?? "",
                  activeSubstance: pi?.activeSubstance ?? "",
                  expiryDate: pi?.expiryDate ?? undefined,
                };
              });

            document.repromaterialProductsJSON = allProductsJson;
            document.repromaterialProducts = repromaterialProducts;
          } catch (err) {
            console.error("Greška pri učitavanju dispozicija:", err);
          }
        }

        setNewDocumentOfReceive(document);
        setPdfData(getRepromaterialInputTemplate(document, pr));
      } catch (error) {
        console.error("Greška pri učitavanju podataka:", error);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Došlo je do greške pri učitavanju podataka"),
          life: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getRepromaterialInputTemplate(
      newDocumentOfReceive,
      productsList,
    );
    setPdfData(newPdf);
  }, [newDocumentOfReceive]);

  const handleChanges = () => {};

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const nameOfProduct = (rowData: any) => {
    return rowData.name;
  };

  const nameOfCategory = (rowData: any) => {
    return rowData?.category?.name;
  };

  if (isLoading) return <ProgressSpinner />;

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-truck header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Pregled prijemnice broj")}:`}{" "}
              {newDocumentOfReceive?.finishedDocumentNumber ??
                newDocumentOfReceive.finishedDocumentId ??
                ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Sve prijemnice")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                navigate(`/repromaterial-input-fast`);
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (
                  newDocumentOfReceive.finishedDocument === true &&
                  newDocumentOfReceive.finishedDocumentId &&
                  newDocumentOfReceive.repromaterialProducts &&
                  newDocumentOfReceive.repromaterialProducts.length > 0 &&
                  newDocumentOfReceive.driverName !== "" &&
                  newDocumentOfReceive.vehicleNumber !== "" &&
                  newDocumentOfReceive.warehouseman !== ""
                ) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti prijemnicu u kojoj nisu uneta sva obavezna polja, koja ne sadrži ni jedan proizvod u listi, koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (
                  newDocumentOfReceive.finishedDocument === true &&
                  newDocumentOfReceive.finishedDocumentId &&
                  newDocumentOfReceive.repromaterialProducts &&
                  newDocumentOfReceive.repromaterialProducts.length > 0 &&
                  newDocumentOfReceive.driverName !== "" &&
                  newDocumentOfReceive.vehicleNumber !== "" &&
                  newDocumentOfReceive.warehouseman !== ""
                ) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati prijemnicu u kojoj nisu uneta sva obavezna polja, koja ne sadrži ni jedan proizvod u listi, koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        <div className="form-body">
          <div className="form-input-fields">
            {activeUser?.role?.name === RoleType.Warehouseman ? (
              <TextInput
                id="warehouse"
                value={activeUser?.warehouse?.name ?? ""}
                onValueChange={handleChanges}
                labelText={t("Magacin")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
            ) : (
              <DropdownInput
                id="warehouse"
                value={newDocumentOfReceive.warehouse.id}
                onValueChange={handleChanges}
                optionValue="id"
                optionLabel="name"
                options={warehousesList}
                labelText={t("Magacin")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
            )}
            <DateInput
              id="date"
              value={
                newDocumentOfReceive?.finishedDocumentFirstTime
                  ? (newDocumentOfReceive.date as Date)
                  : new Date()
              }
              onValueChange={handleChanges}
              labelText={t("Datum")}
              placeholderText={t("Izaberite datum")}
              icon="bi bi-calendar-check"
              maxDate={new Date()}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <TextInput
              id="warehouseman"
              value={newDocumentOfReceive.warehouseman as string}
              onValueChange={handleChanges}
              labelText={t("Magacioner")}
              placeholderText={t("Unesite ime magacionera")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <DateInput
              id="time"
              value={
                newDocumentOfReceive?.finishedDocumentFirstTime
                  ? (newDocumentOfReceive.time as Date)
                  : new Date()
              }
              onValueChange={handleChanges}
              labelText={t("Vreme")}
              placeholderText={t("Izaberite vreme")}
              timeOnly={true}
              icon="bi bi-clock"
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <div className="divider-line">
              <Divider />
            </div>
            <DropdownInput
              id="partner"
              value={newDocumentOfReceive.partner?.id as string}
              optionValue="id"
              optionLabel="name"
              onValueChange={handleChanges}
              options={partnersList}
              filter={true}
              labelText={t("Dobavljač")}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <div className="new-partner-button"></div>
            <DropdownInput
              id="shipper"
              value={newDocumentOfReceive.shipper?.id as string}
              optionValue="id"
              optionLabel="name"
              onValueChange={handleChanges}
              options={partnersList}
              filter={true}
              labelText={t("Prevoznik")}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <TextInput
              id="driverName"
              value={newDocumentOfReceive.driverName as string}
              onValueChange={handleChanges}
              labelText={t("Ime vozača")}
              placeholderText={t("Unesite ime vozača")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <TextInput
              id="vehicleNumber"
              value={(
                newDocumentOfReceive.vehicleNumber as string
              )?.toUpperCase()}
              onValueChange={handleChanges}
              labelText={t("Registarski broj vozila")}
              placeholderText={t("Unesite registarski broj")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            <TextInput
              id="shippingSlip"
              value={newDocumentOfReceive.shippingSlip as string}
              onValueChange={handleChanges}
              labelText={t("Otpremni listić")}
              placeholderText=""
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={true}
            />
            {/* <TextInput
              id='sentQuantity'
              value={newDocumentOfReceive.sentQuantity as number ?? ''}
              onValueChange={handleChanges}
              labelText={t('Upućena količina')}
              placeholderText=''
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter a number',
                }
              }}
              disabled={true}
            /> */}
            {/* <div className="empty-field"></div> */}
            <div className="data-table-container">
              <DataTable value={productsDataTable}>
                <Column
                  body={nameOfProduct}
                  header={`${t("Naziv proizvoda")}:`}
                ></Column>
                <Column
                  body={nameOfCategory}
                  header={`${t("Tip proizvoda")}:`}
                ></Column>
                <Column field="lot" header={`${t("Partija")}:`}></Column>
                <Column field="amount" header={`${t("Količina")}:`}></Column>
                <Column field="measuringUnit" header={`${t("M.J:")}`}></Column>
              </DataTable>
            </div>
            <RadioButtonInput
              id="foreignProduct"
              value={newDocumentOfReceive.foreignProduct}
              radioButtonValue1={t("DA")}
              radioButtonValue2={t("NE")}
              onValueChange={handleChanges}
              labelText={t("Tuđa roba na skladištenju")}
              control={control}
              errors={errors}
              disabled={true}
            />
            {/* {newDocumentOfReceive.foreignProduct === true &&
              <DropdownInput
                id='productOwner'
                value={newDocumentOfReceive.productOwner?.id as string}
                optionValue='id'
                optionLabel='name'
                onValueChange={handleChanges}
                options={partners}
                filter={true}
                labelText={`${t('Vlasnik robe')}`}
                placeholderText={t('Izaberite iz liste')}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
            } */}
            <div className="note-field">
              <TextAreaInput
                id="note"
                value={newDocumentOfReceive.note as string}
                onValueChange={handleChanges}
                rows={2}
                cols={50}
                labelText={t("Napomena")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
            </div>
          </div>
          <div className="documents-preview">
            <PdfPreview
              pdfDefinition={pdfData}
              downloadDocument={downloadPdfDocument}
              printDocument={printPdfDocument}
              documentName={`Prijemnica_${
                newDocumentOfReceive.finishedDocumentId ?? ""
              }`}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
